import React from "react";

import {
  CollapsibleCard,
  Input,
  Select,
  Button,
  IconTrash
} from "@busbud/horizon";

import type { PassengerGender } from "@app/constants/passengers";
import { useAppContext } from "@app/helpers/hooks";

import type { Field } from "../usePassengerForm";

interface Props {
  first_name: Field<string>;
  is_default_expanded?: boolean;
  last_name: Field<string>;
  gender?: Field<PassengerGender>;
  placeholder_name?: string;
  handleChange: (field: string, evt: any) => void;
  onDelete?: () => void;
}

export const PassengerForm: React.FC<Props> = ({
  first_name,
  is_default_expanded = true,
  last_name,
  gender,
  placeholder_name,
  handleChange,
  onDelete
}) => {
  const { translator } = useAppContext();

  const passenger_name =
    placeholder_name || `${first_name.value} ${last_name.value}`;
  const gender_label = `${translator.t(
    "!accounts.passenger-profiles.form.gender"
  )} ${translator.t("!accounts.passenger-profiles.form.optional")}`;

  return (
    <CollapsibleCard
      as="section"
      renderHeader={headerProps => (
        <CollapsibleCard.Header
          {...headerProps}
          buttonAriaLabel={translator.t("!accounts.settings.expand-content")}
        >
          <CollapsibleCard.Title as="h2">
            {passenger_name}
          </CollapsibleCard.Title>
        </CollapsibleCard.Header>
      )}
      isInitiallyExpanded={is_default_expanded}
    >
      <CollapsibleCard.Body>
        <div className="flex flex-col gap-100">
          <Input
            name="first_name"
            labelText={translator.t("!accounts.edit-profile.first-name")}
            value={first_name.value}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
              handleChange("first_name", e)
            }
            isInvalid={!!first_name.has_error && !!first_name.error}
            errorText={first_name.error?.message || ""}
          />

          <Input
            name="last_name"
            labelText={translator.t("!accounts.edit-profile.last-name")}
            value={last_name.value}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
              handleChange("last_name", e)
            }
            isInvalid={last_name.has_error}
            errorText={last_name.error?.message || ""}
          />

          {!!gender && (
            <Select
              labelText={gender_label}
              value={gender.value}
              isInvalid={gender.has_error}
              onChange={e => handleChange("gender", e)}
            >
              <Select.Option value="" />
              <Select.Option value="male">
                {translator.t("!accounts.passenger-profiles.form.gender.male")}
              </Select.Option>
              <Select.Option value="female">
                {translator.t(
                  "!accounts.passenger-profiles.form.gender.female"
                )}
              </Select.Option>
            </Select>
          )}
          {!!onDelete && (
            <Button
              variant="text"
              appearance="destructive"
              iconStart={<IconTrash size="md" />}
              className="w-full"
              size="lg"
              onClick={onDelete}
            >
              {translator.t("!accounts.passengers.delete-passenger")}
            </Button>
          )}
        </div>
      </CollapsibleCard.Body>
    </CollapsibleCard>
  );
};
